import {
  CompanyPayrollDeductionApi,
  PayrollDeductionData,
} from '../../api/apis/CompanyPayrollDeductionApi';
import type { IAppStorage } from '@aesop-fables/scrinium';
import {
  ConfiguredDataSource,
  DataCompartmentOptions,
  createDataCache,
  createDataCacheModule,
} from '@aesop-fables/scrinium';
import type { IServiceContainer } from '@aesop-fables/containr';
import { firstValueFrom } from 'rxjs';
import { ApiKeys } from '../../api/apis/ApiKeys';
import { GroupFeatureMapCompartments, groupFeatureMapStorageKey } from '../groupFeatureMap';
import { GroupFeatureMapData } from '../../models/GroupFeatureMapData';
import { OrganizationCompartments, organizationStorageKey } from '../organization';
import { OrganizationData } from '../../models/OrganizationData';
import { OrgAdminRolesResolved } from '../../predicates/OrgAdminRolesResolved';

export interface PayrollDeductionCompartments {
  deductions: DataCompartmentOptions<PayrollDeductionData[] | undefined>;
}

export const payrollDeductionStorageKey = 'data/deductions';

export const withPayrollDeductionData = createDataCacheModule(
  (appStorage: IAppStorage, container: IServiceContainer) => {
    const cache = createDataCache<PayrollDeductionCompartments>({
      deductions: {
        loadingOptions: {
          strategy: 'lazy',
          predicate: container.resolve(OrgAdminRolesResolved),
        },
        source: new ConfiguredDataSource(async () => {
          const groupFeatureMapDataCache =
            appStorage.retrieve<GroupFeatureMapCompartments>(groupFeatureMapStorageKey);
          const groupFeatureMap = await firstValueFrom(
            groupFeatureMapDataCache.observe$<GroupFeatureMapData>('groupFeatureMap'),
          );

          const orgDataCache =
            appStorage.retrieve<OrganizationCompartments>(organizationStorageKey);
          const organization = await firstValueFrom(
            orgDataCache.observe$<OrganizationData>('organization'),
          );

          if (groupFeatureMap?.enableCompanyPayrollInfo && organization) {
            const payrollDeductionApi = container.get<CompanyPayrollDeductionApi>(
              ApiKeys.PayrollDeduction,
            );
            const { data } = await payrollDeductionApi.get();
            if (data) {
              return [...data];
            }
          }
          return undefined;
        }),
        defaultValue: undefined,
      },
    });

    appStorage.store(payrollDeductionStorageKey, cache);
  },
);
